import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, ShieldCheck, Trophy, MapPin, Gem, Video, Users, ClipboardList } from "lucide-react";
import AdBanner from "@/components/AdBanner";
import { Editable } from "@/context/ContentContext";
import { api } from "@/lib/api";

const BADGE_COLORS = { Obsidian: "#A1A1AA", "Lapis Lazuli": "#60A5FA", Jade: "#34D399", Sapphire: "#38BDF8", Diamond: "#E0F2FE" };

const APP_URL = "https://www.picklebullpen.com";
const SIGNUP_HREF = window.location.hostname.includes("picklebullpin")
  ? `${APP_URL}/auth?mode=register`
  : "/auth?mode=register";
const BG_URL =
  "https://static.prod-images.emergentagent.com/jobs/ccb7baf7-4fe3-4351-b3c9-d8e5a28ac676/images/3600674333cdf05b2fe0c4e685c1ff92c155a3dd1d3c8d44ced91c1e6be722a5.jpeg";

const JoinButton = ({ testId, large }) => (
  <a
    href={SIGNUP_HREF}
    data-testid={testId}
    className={`inline-flex items-center gap-2 rounded-full bg-lime-400 text-black font-display font-bold uppercase tracking-wider shadow-[0_8px_30px_rgba(163,230,53,0.45)] transition-transform duration-200 hover:scale-105 hover:bg-lime-300 ${
      large ? "px-10 py-4 text-xl sm:text-2xl" : "px-6 py-2.5 text-base sm:text-lg"
    }`}
  >
    Enter the Bullpen <ArrowRight className={large ? "w-6 h-6" : "w-5 h-5"} />
  </a>
);

const Section = ({ title, children }) => (
  <section className="max-w-3xl mx-auto px-6 py-10">
    {title && (
      <h2 className="font-display font-extrabold uppercase tracking-wide text-lime-300 text-2xl sm:text-3xl mb-4">
        {title}
      </h2>
    )}
    <div className="space-y-4 text-base sm:text-lg leading-relaxed text-neutral-100">{children}</div>
  </section>
);

const PublicLeaderboard = () => {
  const [lb, setLb] = useState(null);
  useEffect(() => {
    api.get("/psi/leaderboard/public").then(({ data }) => setLb(data)).catch(() => setLb({ leaders: [] }));
  }, []);
  if (!lb || lb.leaders.length < 3) return null;
  return (
    <section className="max-w-3xl mx-auto px-6 py-10" data-testid="public-leaderboard">
      <h2 className="font-display font-extrabold uppercase tracking-wide text-lime-300 text-2xl sm:text-3xl mb-2 text-center">
        The Bullpen Leaderboard
      </h2>
      <p className="text-center text-sm text-neutral-300 mb-6">
        {lb.total} players grinding the Performance Index right now. Live top 10 — earn your gem.
      </p>
      <div className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 divide-y divide-white/10 overflow-hidden">
        {lb.leaders.map((r) => (
          <div key={r.rank} data-testid={`public-lb-row-${r.rank}`}
            className="flex items-center gap-3 px-4 sm:px-6 py-3 hover:bg-white/5 transition-colors">
            <span className={`font-display font-black text-lg w-7 text-center shrink-0 ${r.rank <= 3 ? "text-lime-400" : "text-neutral-400"}`}>{r.rank}</span>
            {r.avatar ? (
              <img src={r.avatar} alt={r.name} className="w-8 h-8 rounded-full object-cover border border-white/20 shrink-0" />
            ) : (
              <span className="w-8 h-8 rounded-full bg-lime-400/20 border border-lime-400/40 flex items-center justify-center text-xs font-black text-lime-300 shrink-0">
                {(r.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </span>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm sm:text-base truncate">{r.name}</div>
              <div className="text-[11px] text-neutral-400 truncate">{r.division}{r.location ? ` · ${r.location}` : ""}</div>
            </div>
            {r.badge && (
              <span className="hidden sm:flex items-center gap-1 text-[10px] font-black uppercase tracking-widest shrink-0"
                style={{ color: BADGE_COLORS[r.badge] || "#A1A1AA" }}>
                <Gem className="w-3 h-3" /> {r.badge}
              </span>
            )}
            <span className="font-display font-black text-xl text-lime-400 shrink-0">{r.psi}</span>
          </div>
        ))}
      </div>
      <div className="text-center mt-6">
        <JoinButton testId="join-from-leaderboard-btn" />
      </div>
    </section>
  );
};

function CodeShimmer() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const CELL = 18, CHARS = "PICKLEBULPEN01<>+*#$%".split("");
    let raf, w, h, cols, drops;
    const setup = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      cols = Math.ceil(w / CELL);
      drops = Array.from({ length: cols }, (_, i) => (i % 3 === 0 ? Math.random() * -(h / CELL) * 2 : null));
    };
    let last = 0;
    const frame = (t) => {
      raf = requestAnimationFrame(frame);
      if (t - last < 70) return;
      last = t;
      ctx.clearRect(0, 0, w, h);
      ctx.font = `${CELL - 4}px monospace`;
      for (let i = 0; i < cols; i++) {
        if (drops[i] === null) continue;
        const row = Math.floor(drops[i]);
        for (let k = 0; k < 6; k++) {
          const r2 = row - k;
          if (r2 < 0) continue;
          const ch = CHARS[(i * 31 + r2 * 7) % CHARS.length];
          if (k === 0) { ctx.fillStyle = "rgba(217,249,157,0.5)"; ctx.shadowColor = "#A3E635"; ctx.shadowBlur = 10; }
          else { ctx.fillStyle = `rgba(74,222,128,${0.26 - k * 0.04})`; ctx.shadowBlur = 0; }
          ctx.fillText(ch, i * CELL, r2 * CELL);
        }
        ctx.shadowBlur = 0;
        drops[i] += 0.5;
        if ((row - 6) * CELL > h) drops[i] = Math.random() * -30;
      }
    };
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setup();
    if (!reduced) raf = requestAnimationFrame(frame);
    const onResize = () => setup();
    window.addEventListener("resize", onResize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
  }, []);
  return (
    <div className="fixed inset-0 pointer-events-none" data-testid="landing-code-shimmer" aria-hidden="true">
      <canvas ref={ref} className="w-full h-full" style={{ mixBlendMode: "screen", opacity: 0.45 }} />
      <div className="absolute inset-0 landing-glow-sweep" style={{ mixBlendMode: "screen" }} />
    </div>
  );
}

export default function Landing() {
  useEffect(() => { localStorage.setItem("pb_seen_welcome", "1"); }, []);  return (
    <div data-testid="bullpin-landing-page" className="relative min-h-screen text-white font-sans">
      {/* Fixed background photo */}
      <div
        className="fixed inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${BG_URL})` }}
        aria-hidden="true"
      />
      {/* Vignette + darkening overlay so text stays crisp */}
      <div
        className="fixed inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(3,6,3,0.30) 0%, rgba(3,5,3,0.55) 60%, rgba(2,4,2,0.85) 100%)",
        }}
        aria-hidden="true"
      />
      <CodeShimmer />

      <div className="relative z-10">
        {/* Top bar */}
        <header className="flex items-center justify-between max-w-5xl mx-auto px-6 py-5">
          <div className="font-display font-black uppercase tracking-widest text-lg sm:text-xl">
            The Pickle <span className="text-lime-400">Bullpin</span> LLC
          </div>
          <JoinButton testId="join-bullpen-top-btn" />
        </header>

        {/* Hero */}
        <div className="max-w-4xl mx-auto px-6 pt-16 pb-10 text-center">
          <p className="font-mono text-lime-300 uppercase tracking-[0.3em] text-xs sm:text-sm mb-5">
            <Editable ck="landing.hero.eyebrow" fallback="Built in Florida · Est. January 2025" />
          </p>
          <h1 className="font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
            <Editable ck="landing.hero.title" fallback="Welcome to The Pickle Bullpen" />
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-lime-100 font-semibold">
            <Editable ck="landing.hero.subtitle" fallback="Where Discipline Meets Development — And Players Are Built, Not Born." />
          </p>
          <a href="/events" data-testid="landing-events-link"
            className="inline-flex items-center gap-2 mt-6 border border-lime-400/50 text-lime-300 text-xs font-black uppercase tracking-widest px-6 py-3 rounded-full hover:bg-lime-400/10 transition-colors">
            📅 Upcoming Tournaments &amp; Moneyball Events →
          </a>
        </div>

        {/* Intro */}
        <Section>
          <p>
            This isn't another pickleball app. The Bullpen is where your game gets built — one rep, one drill,
            one match at a time. It was made for the player who stays after open play to work on their third shot.
            The coach who cares more about progress than praise. The tournament director who wants game day to
            run like clockwork.
          </p>
          <p>
            We believe improvement isn't luck — it's earned. So we built an engine that tracks the work: every
            session, every drill, every league night and tournament feeds a live Player Skill Index that reflects
            who you actually are on the court. No talk. No guesswork. Just your work, measured and displayed.
          </p>
          <p className="text-sm text-neutral-300">
            The Pickle Bullpin LLC has been officially operating since January 2025 under the State of Florida —
            a real, registered operation with real people behind it, on the court doing the work right beside you.
          </p>
        </Section>

        {/* Who it's for */}
        <section className="max-w-5xl mx-auto px-6 py-10">
          <h2 className="font-display font-extrabold uppercase tracking-wide text-lime-300 text-2xl sm:text-3xl mb-2 text-center">
            Built for the People Who Do the Work
          </h2>
          <p className="text-center text-base sm:text-lg text-neutral-200 mb-8">
            Players. Coaches. Tournament directors. One engine, three ways in.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <div data-testid="audience-players" className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 p-6 transition-transform duration-200 hover:-translate-y-1">
              <Video className="w-8 h-8 text-lime-400 mb-3" />
              <h3 className="font-display font-bold uppercase tracking-wider text-lg text-white mb-2">For Players</h3>
              <ul className="space-y-2 text-sm text-neutral-200 leading-relaxed">
                <li>· A live <span className="text-lime-300 font-semibold">Player Skill Index</span> that grows with every rep, match and league night</li>
                <li>· AI film breakdowns of your matches and drills — see what to fix, then fix it</li>
                <li>· Leagues, tournaments and Moneyball events where your work pays off</li>
                <li>· Your own AI sports card, a community of grinders, and a leaderboard with your name on it</li>
              </ul>
            </div>
            <div data-testid="audience-coaches" className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 p-6 transition-transform duration-200 hover:-translate-y-1">
              <Users className="w-8 h-8 text-lime-400 mb-3" />
              <h3 className="font-display font-bold uppercase tracking-wider text-lg text-white mb-2">For Coaches</h3>
              <ul className="space-y-2 text-sm text-neutral-200 leading-relaxed">
                <li>· Get booked and get paid — players find you, schedule you and pay you right in the app</li>
                <li>· See your players' real numbers, so every session targets what actually needs work</li>
                <li>· A Coaches Lounge built around your craft — structured drills, not chaos</li>
                <li>· Your reputation grows with your players — their progress is your proof</li>
              </ul>
            </div>
            <div data-testid="audience-directors" className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 p-6 transition-transform duration-200 hover:-translate-y-1">
              <ClipboardList className="w-8 h-8 text-lime-400 mb-3" />
              <h3 className="font-display font-bold uppercase tracking-wider text-lg text-white mb-2">For Tournament Directors</h3>
              <ul className="space-y-2 text-sm text-neutral-200 leading-relaxed">
                <li>· Run your whole event in one place — registration, divisions, brackets, payouts</li>
                <li>· QR self check-in and a live court board that keeps players moving, not asking</li>
                <li>· Champions get celebrated automatically — feed announcements, bounty cards, bragging rights</li>
                <li>· Less clipboard, more pickleball</li>
              </ul>
            </div>
          </div>
        </section>

        <Section title="The Standard: Do the Work">
          <p>
            Everything in the Bullpen runs on one belief: <span className="font-semibold text-white">discipline,
            dedication and doing the work.</span> There are no shortcuts hiding in here. The Player Skill Index
            can't be talked up — it moves when you move. The film doesn't flatter you — it shows you the truth.
            The leaderboard doesn't care who you know — it cares what you did.
          </p>
          <p>
            That's the point. When the work is what gets measured, the work is what gets done. Players who train
            with intention get better, faster. Coaches who develop players get busier. Directors who run tight
            events get bigger ones. Everybody eats — because everybody earns it.
          </p>
          <p className="font-semibold text-white">
            We are not here to sell hype.
            <br />
            We are here to build players.
          </p>
        </Section>

        {/* Pillars */}
        <section className="max-w-4xl mx-auto px-6 py-10">
          <h2 className="font-display font-extrabold uppercase tracking-wide text-lime-300 text-2xl sm:text-3xl mb-6 text-center">
            Our Standard: Quality, Discipline, Legacy
          </h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { icon: Target, title: "Quality", desc: "Precision in training, systems, and tools" },
              { icon: ShieldCheck, title: "Discipline", desc: "Structured development that creates real progress" },
              { icon: Trophy, title: "Legacy", desc: "Building something that lasts beyond today's trends" },
            ].map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                data-testid={`pillar-${title.toLowerCase()}`}
                className="rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 p-6 text-center transition-transform duration-200 hover:-translate-y-1"
              >
                <Icon className="w-8 h-8 text-lime-400 mx-auto mb-3" />
                <h3 className="font-display font-bold uppercase tracking-wider text-lg text-white">{title}</h3>
                <p className="mt-2 text-sm text-neutral-200">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center text-base sm:text-lg text-neutral-100 space-y-1">
            <p>Quality in how we train. Discipline in how we compete. Legacy in who we become.</p>
            <p>
              That's the Bullpen standard — and it belongs to every player, coach and director who
              shows up and puts in the work.
            </p>
          </div>
        </section>

        {/* Live Top 10 Leaderboard */}
        <PublicLeaderboard />

        {/* Final CTA */}
        <section className="max-w-3xl mx-auto px-6 py-14 text-center">
          <h2 className="font-display font-black uppercase text-3xl sm:text-4xl text-white mb-5">
            This Is Your Entry Point
          </h2>
          <div className="text-base sm:text-lg text-neutral-100 space-y-1 mb-4">
            <p>If you're a player ready to stop guessing and start measuring—</p>
            <p>If you're a coach who builds players, not just fills court time—</p>
            <p>If you're a director who wants game day to run itself—</p>
          </div>
          <p className="font-display font-bold uppercase tracking-wide text-xl sm:text-2xl text-lime-300 mb-2">
            Welcome to The Pickle Bullpen.
          </p>
          <p className="text-lg text-neutral-200 mb-8">Your work starts here.</p>
          <JoinButton testId="join-bullpen-bottom-btn" large />
        </section>

        {/* SEO: service areas & offerings */}
        <div className="max-w-4xl mx-auto px-6"><AdBanner zone="landing" /></div>
        <section className="max-w-4xl mx-auto px-6 py-10" data-testid="seo-service-area">
          <h2 className="font-display font-extrabold uppercase tracking-wide text-lime-300 text-2xl sm:text-3xl mb-4 text-center">
            Pickleball Training, Leagues, Tournaments &amp; Moneyball Events
          </h2>
          <div className="space-y-4 text-base sm:text-lg leading-relaxed text-neutral-100 text-center">
            <p>
              The Pickle Bullpen runs structured pickleball training programs, competitive leagues, sanctioned-style
              tournaments and high-stakes <span className="text-lime-300 font-semibold">Moneyball pickleball events</span> across
              the Florida Suncoast — proudly serving <span className="font-semibold text-white">Bradenton, Sarasota, Lakewood
              Ranch, Ellenton, Manatee County, St. Petersburg and Tampa</span>.
            </p>
            <p>
              Every player gets a live Player Skill Index, AI-powered video film analysis, coach-led development and a
              digital player card that tracks their rise. Built in Florida — open to pickleball players everywhere.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {["Bradenton", "Sarasota", "Lakewood Ranch", "Ellenton", "Manatee County", "St. Petersburg", "Tampa"].map((c) => (
              <span key={c} className="px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-bold uppercase tracking-widest text-neutral-200">
                <MapPin className="w-3 h-3 inline mr-1 text-lime-400" />{c}
              </span>
            ))}
          </div>
        </section>

        <footer className="border-t border-white/10 py-6 text-center text-xs text-neutral-400">
          <p className="flex items-center justify-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> The Pickle Bullpin LLC · Port Charlotte, Florida · Operating since January 2025
          </p>
          <p className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 uppercase tracking-widest text-[10px]" data-testid="landing-legal">
            <Link to="/privacy-policy" className="hover:text-lime-400">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-lime-400">Terms of Service</Link>
            <Link to="/return-policy" className="hover:text-lime-400">Return Policy</Link>
          </p>
        </footer>
      </div>
    </div>
  );
}
